import { useState, useEffect, useContext } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { SocketContext } from "../../context/socket";
import useStandingsHandler from "../../hooks/useStandingsHandler";
import StandingsSelect from "../headtohead/StandingsSelect";

export default function TeamAggregateGrid({
   loadDownloader,
   clearDownloader
}) {
   const socket = useContext(SocketContext);
   const [selectedRound, setSelectedRound] = useState(0);
   const [teamStandings, setTeamStandings] = useState([]);
   const [roundName, setRoundName] = useState("");

   const [
      standings,
      setStandings,
      cachedStandings,
      tournamentRounds,
      notificationStandings,
      {
         getCachedStandings,
         getTournamentRounds,
      },
   ] = useStandingsHandler();

   useEffect(() => {
      getCachedStandings();
   }, []);

   useEffect(() => {
      if (selectedRound !== null && selectedRound !== undefined) {
         // Handle Round 0 (Alphabetical) - get all teams
         if (selectedRound === 9999999 || selectedRound === '9999999') {
            socket.emit("getPlayers", (allPlayers) => {
               if (allPlayers && allPlayers.length > 0) {
                  // Get unique teams
                  const teamSet = new Set();
                  allPlayers.forEach(player => {
                     const team = player.TEAM;
                     console.log(team);
                     if (team && team.trim() !== "" && !team.includes("Strixhaven")) {
                        teamSet.add(team);
                     }
                
                  });
                  
                  // Convert to array and sort alphabetically
                  const sortedTeams = Array.from(teamSet).sort((a, b) => {
                     return a.localeCompare(b);
                  });
                  
                  // Create team standings with rank and 0 stats
                  const alphabeticalStandings = sortedTeams.map((team, index) => ({
                     Rank: index + 1,
                     Team: team,
                     Win: 0,
                     Loss: 0,
                     Draw: 0,
                     WinPercent: 0,
                  }));
                  
                  setTeamStandings(alphabeticalStandings);
                  setRoundName(""); // No round name for alphabetical
               } else {
                  setTeamStandings([]);
               }
            });
            return;
         }
         const roundId = selectedRound === 0 || selectedRound === '0' ? 0 : Number(selectedRound);
         socket.emit("getTeamStandings", roundId, (response) => {
            const standings = response?.teamStandings || response || [];
            const round = response?.Round || "";
            setTeamStandings(standings);
            setRoundName(round);
         });
      }
   }, [selectedRound]);

   const handleStandingsChange = (data) => {
      if (data && data.id !== null && data.id !== undefined) {
         // Round 0 (Alphabetical) is value 9999999
         const roundId = data.id === '0' ? 0 : (data.id === '9999999' ? 9999999 : Number(data.id));
         setSelectedRound(roundId);
      }
   };

   let columns;
   let initialState;

   columns = [
      {
         field: "Rank",
         headerName: "Rank",
         editable: false,
         width: 100,
         filterable: false,
      },
      {
         field: "Team",
         headerName: "Team",
         editable: false,
         width: 300,
      },
      {
         field: "Win",
         headerName: "Win",
         editable: false,
         width: 100,
      },
      {
         field: "Loss",
         headerName: "Loss",
         editable: false,
         width: 100,
      },
      {
         field: "Draw",
         headerName: "Draw",
         editable: false,
         width: 100,
      },
      {
         field: "WinPercent",
         headerName: "Win %",
         editable: false,
         width: 120,
         valueGetter: (params) => params.row.WinPercent ? params.row.WinPercent.toFixed(1) + '%' : '0.0%',
      },
   ];

   initialState = {
      sorting: {
         sortModel: [{ field: "Rank", sort: "asc" }],
      },
   };

   const getRowId = (row) => row.Rank + "-" + row.Team;

   const refreshGrid = () => {
      getCachedStandings();
      if (selectedRound !== null && selectedRound !== undefined) {
         const roundId = selectedRound === 0 || selectedRound === '0' ? 0 : Number(selectedRound);
         socket.emit("getTeamStandings", roundId, (response) => {
            const standings = response?.teamStandings || response || [];
            const round = response?.Round || "";
            setTeamStandings(standings);
            setRoundName(round);
         });
      }
      clearDownloader();
   };

   return (
      <>
         <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h3" component="div" sx={{ textTransform: "uppercase" }}>Team Aggregate</Typography>
            <Stack direction="row" spacing={2} alignItems="center">
               <StandingsSelect 
                  selected={selectedRound}
                  callback={handleStandingsChange}
               />
               <Button 
                  variant="contained"
                  color="primary"
                  onClick={() => {
                     const roundId = selectedRound === 0 || selectedRound === '0' ? 0 : (selectedRound === 9999999 || selectedRound === '9999999' ? 9999999 : Number(selectedRound));
                     const teamStandingsData = { 
                        teamStandings: teamStandings,
                        Round: roundName || (roundId === 9999999 ? "" : roundName)
                     };
                     loadDownloader("teamaggregate", roundId, JSON.stringify(teamStandingsData), roundId === 9999999 ? "TeamAggregate_Round0_Alphabetical" : "TeamAggregate_Round" + roundId);
                  }}
                  disabled={!teamStandings || teamStandings.length === 0}
               >
                  LOAD
               </Button>
               <Button onClick={refreshGrid} color="error" variant="outlined">Refresh Data</Button>
            </Stack>
         </Stack>
         <Box height={400} width="100%">
            {teamStandings && teamStandings.length > 0 && (
               <DataGrid
                  initialState={initialState}
                  sx={{ backgroundColor: "#efefef" }}
                  rows={teamStandings}
                  rowHeight={40}
                  columns={columns}
                  getRowId={getRowId}
                  rowsPerPageOptions={[100, 200, 300, 1000, 5000]}
                  components={{ Toolbar: GridToolbar }}
                  componentsProps={{
                     toolbar: {
                        printOptions: {
                           disableToolbarButton: true,
                        },
                     },
                  }}
                  disableColumnSelector
                  disableDensitySelector
                  disableSelectionOnClick
               />
            )}
         </Box>
      </>
   );
}
